// ─────────────────────────────────────────────────────────────
// PatientReport.jsx
// Generates a printable PDF-style patient report
// Usage: import { printPatientReport } from "./PatientReport";
//        then call printPatientReport(patient, clinicSettings)
// ─────────────────────────────────────────────────────────────

import { supabase } from "./supabaseclient";
function getPainLabel(score) {
  if (score >= 7) return "Severe";
  if (score >= 4) return "Moderate";
  return "Mild";
}

export async function printPatientReport(
  patient,
  clinic = {
    clinic_name: "Clinic Report",
    owner_name: "",
    phone: "",
    email: "",
    address: "",
  }
) {
  const clinicName = clinic?.clinic_name || "Clinic Report";
  const clinicalInchargeName = clinic?.owner_name || "";
  const clinicPhone = clinic?.phone || "";
  const clinicEmail = clinic?.email || "";
  const clinicAddress = clinic?.address || "";
  const signatureName = clinic?.signature_name || "";
  const designation = clinic?.designation || "";
  const signatureImageUrl = clinic?.signature_image_url || "";
  let signaturePreviewUrl = "";

if (signatureImageUrl) {
  const { data } = await supabase.storage
    .from("user-signatures")
    .createSignedUrl(signatureImageUrl, 300);

  signaturePreviewUrl = data?.signedUrl || "";
}

  const progress = Math.min(
    Math.round((patient.sessions / Math.max(patient.totalSessions, 1)) * 100),
    100
  );

  const painScore = patient.painScore ?? 0;
  const painLabel = getPainLabel(painScore);

  const html = `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="UTF-8"/>
    <title></title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'DM Sans', sans-serif; background: #fff; color: #1a1a2e; padding: 40px; font-size: 13px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #c9a96e; padding-bottom: 20px; margin-bottom: 28px; }
      .clinic-name { font-family: 'Cormorant Garamond', serif; font-size: 28px; font-weight: 700; color: #c9a96e; }
      .clinic-sub { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: #888; margin-top: 3px; }
      .clinic-meta { margin-top: 10px; font-size: 11px; color: #777; line-height: 1.7; }
      .report-title { text-align: right; }
      .report-title h2 { font-family: 'Cormorant Garamond', serif; font-size: 22px; color: #1a1a2e; }
      .report-title p { font-size: 11px; color: #888; margin-top: 4px; }
      .patient-hero { background: linear-gradient(135deg, #1a1a2e, #0f3460); color: #fff; border-radius: 12px; padding: 24px 28px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: center; }
      .patient-name { font-family: 'Cormorant Garamond', serif; font-size: 28px; font-weight: 700; color: #c9a96e; }
      .patient-sub { font-size: 12px; color: #aaa; margin-top: 4px; }
      .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 10px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; margin-top: 10px; }
      .badge-active { background: rgba(16,185,129,0.2); color: #34d399; }
      .badge-completed { background: rgba(99,102,241,0.2); color: #a5b4fc; }
      .badge-severe { background: rgba(248,113,113,0.2); color: #f87171; }
      .badge-moderate { background: rgba(251,191,36,0.2); color: #fbbf24; }
      .badge-mild { background: rgba(52,211,153,0.2); color: #34d399; }
      .progress-box { text-align: right; }
      .progress-label { font-size: 11px; color: #aaa; margin-bottom: 6px; }
      .progress-bar-bg { width: 180px; height: 8px; background: rgba(255,255,255,0.15); border-radius: 4px; overflow: hidden; margin-left: auto; }
      .progress-bar-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, #c9a96e, #e8c98a); }
      .progress-pct { font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 700; color: #c9a96e; margin-top: 6px; text-align: right; }
      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
      .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin-bottom: 20px; }
      .section { background: #f8f9fc; border: 1px solid #e8eaf0; border-radius: 10px; padding: 20px; }
      .section-title { font-size: 9px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: #c9a96e; margin-bottom: 14px; border-bottom: 1px solid #e8eaf0; padding-bottom: 8px; }
      .field { margin-bottom: 10px; }
      .field-label { font-size: 10px; color: #888; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; }
      .field-value { font-size: 13px; font-weight: 500; color: #1a1a2e; }
      .vital-card { background: #fff; border: 1px solid #e8eaf0; border-radius: 8px; padding: 16px; text-align: center; }
      .vital-value { font-family: 'Cormorant Garamond', serif; font-size: 28px; font-weight: 700; color: #1a1a2e; }
      .vital-unit { font-size: 10px; color: #888; margin-top: 2px; }
      .vital-label { font-size: 11px; color: #555; margin-top: 6px; font-weight: 500; }
      .history-item { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid #e8eaf0; }
      .history-bar { width: 3px; background: #c9a96e; border-radius: 2px; flex-shrink: 0; }
      .history-text { font-size: 12px; color: #555; line-height: 1.6; }
      .notes-box { background: #fff; border-left: 3px solid #c9a96e; border-radius: 0 8px 8px 0; padding: 16px 20px; font-size: 13px; color: #555; line-height: 1.8; }
      .billing-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #e8eaf0; }
      .billing-label { font-size: 12px; color: #555; }
      .billing-value { font-size: 13px; font-weight: 600; color: #1a1a2e; }
      .billing-total { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 700; color: #c9a96e; }
      .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e8eaf0; display: flex; justify-content: space-between; font-size: 10px; color: #aaa; }
      @media print {
        body { padding: 20px; }
        @page { margin: 15mm; }
      }
    </style>
  </head>
  <body>
  
    <!-- HEADER -->
    <div class="header">
      <div>
        <div class="clinic-name">⚕ ${clinicName}</div>
        <div class="clinic-sub">Physiotherapy & Rehabilitation</div>
        ${
          clinicalInchargeName || clinicPhone || clinicEmail || clinicAddress
            ? `
          <div class="clinic-meta">
            ${clinicalInchargeName ? `<div><strong>Clinical Incharge:</strong> ${clinicalInchargeName}</div>` : ""}
            ${clinicPhone ? `<div><strong>Phone:</strong> ${clinicPhone}</div>` : ""}
            ${clinicEmail ? `<div><strong>Email:</strong> ${clinicEmail}</div>` : ""}
            ${clinicAddress ? `<div><strong>Address:</strong> ${clinicAddress}</div>` : ""}
          </div>
        `
            : ""
        }
      </div>
      <div class="report-title">
        <h2>Patient Report</h2>
        <p>Generated: ${new Date().toLocaleDateString("en-IN", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}</p>
        <p>Patient ID: ${patient.patientId || "Not assigned"}</p>
      </div>
    </div>
  
    <!-- PATIENT HERO -->
    <div class="patient-hero">
      <div>
        <div class="patient-name">${patient.name}</div>
        <div class="patient-sub">${patient.age} yrs · ${patient.gender}${
    patient.blood && patient.blood !== "—" ? ` · Blood: ${patient.blood}` : ""
  }</div>
        <span class="badge badge-${patient.status?.toLowerCase()}">${
    patient.status
  }</span>
        <span class="badge badge-${painLabel.toLowerCase()}" style="margin-left:6px;">NPRS ${painScore}/10 · ${painLabel}</span>
      </div>
      <div class="progress-box">
        <div class="progress-label">Treatment Progress</div>
        <div class="progress-bar-bg">
          <div class="progress-bar-fill" style="width:${progress}%"></div>
        </div>
        <div class="progress-pct">${progress}%</div>
        <div style="font-size:11px;color:#aaa;text-align:right;">${
          patient.sessions
        } of ${patient.totalSessions} sessions</div>
      </div>
    </div>
  
    <!-- PERSONAL + CLINICAL -->
    <div class="grid-2">
      <div class="section">
        <div class="section-title">Personal Information</div>
        <div class="field"><div class="field-label">Phone</div><div class="field-value">${
          patient.phone || "Not recorded"
        }</div></div>
        <div class="field"><div class="field-label">Email</div><div class="field-value">${
          patient.email || "Not recorded"
        }</div></div>
        <div class="field"><div class="field-label">Address</div><div class="field-value">${
          patient.address || "Not recorded"
        }</div></div>
        <div class="field"><div class="field-label">Registered On</div><div class="field-value">${
          patient.joinDate
        }</div></div>
      </div>

      <div class="section">
        <div class="section-title">Clinical Details</div>
        <div class="field"><div class="field-label">Diagnosis</div><div class="field-value" style="font-size:15px;font-weight:600;">${
          patient.condition
        }</div></div>
        <div class="field"><div class="field-label">Assigned Therapist</div><div class="field-value">${
          patient.therapist
        }</div></div>
        <div class="field"><div class="field-label">Next Visit</div><div class="field-value">${
          patient.nextVisit || "Not scheduled"
        }</div></div>
        <div class="field"><div class="field-label">Sessions Prescribed</div><div class="field-value">${
          patient.totalSessions
        }</div></div>
      </div>
    </div>
  
    <!-- VITALS -->
    <div class="section" style="margin-bottom:20px;">
      <div class="section-title">Recorded Vitals</div>
      <div class="grid-2" style="margin-bottom:0;">
        <div class="vital-card">
          <div class="vital-value">${patient.vitals?.bp || "—"}</div>
          <div class="vital-unit">mmHg</div>
          <div class="vital-label">Blood Pressure</div>
        </div>
        <div class="vital-card">
          <div class="vital-value">${patient.vitals?.pulse || "—"}</div>
          <div class="vital-unit">bpm</div>
          <div class="vital-label">Pulse Rate</div>
        </div>
      </div>
    </div>

    <!-- DIAGNOSTICS -->
    <div class="grid-2">
      <div class="section">
        <div class="section-title">Diagnostic Tools</div>
        <div class="notes-box">${patient.diagnosticTools || "Not recorded."}</div>
      </div>

      <div class="section">
        <div class="section-title">Outcome Measures</div>
        <div class="notes-box">${patient.outcomeMeasures || "Not recorded."}</div>
      </div>
    </div>

    <div class="section" style="margin-bottom:20px;">
      <div class="section-title">Special Tests (if any)</div>
      <div class="notes-box">${patient.specialTests || "Not recorded."}</div>
    </div>

    <!-- HISTORIES -->
    <div class="grid-3">
      <div class="section">
        <div class="section-title">Past History</div>
        <div class="notes-box">${patient.pastHistory || "Not recorded."}</div>
      </div>

      <div class="section">
        <div class="section-title">Medical History</div>
        <div class="notes-box">${patient.medicalHistory || "Not recorded."}</div>
      </div>

      <div class="section">
        <div class="section-title">Surgical History</div>
        <div class="notes-box">${patient.surgicalHistory || "Not recorded."}</div>
      </div>
    </div>
  
    <!-- SESSION LOG -->
    ${
      (patient.history || []).length > 0
        ? `
    <div class="section" style="margin-bottom:20px;">
      <div class="section-title">Session Log</div>
      ${(patient.history || [])
        .map(
          (entry) => `
        <div class="history-item">
          <div class="history-bar"></div>
          <div class="history-text">${entry}</div>
        </div>
      `
        )
        .join("")}
    </div>
    `
        : ""
    }
 ${
  signatureName || designation || signaturePreviewUrl
    ? `
<div class="section" style="margin-bottom:20px;">
  <div class="section-title">Authorized Signature</div>
  <div class="notes-box">
    ${signatureName ? `<div><strong>Signed By:</strong> ${signatureName}</div>` : ""}
    ${designation ? `<div style="margin-top:6px;"><strong>Designation:</strong> ${designation}</div>` : ""}
    ${signaturePreviewUrl ? `<div style="margin-top:12px;"><img src="${signaturePreviewUrl}" alt="Signature" style="max-height:80px; max-width:240px; object-fit:contain;" /></div>` : ""}
  </div>
</div>
`
    : ""
}
    <!-- FOOTER -->
    <div class="footer">
      <div>${clinicName} · Confidential patient report</div>
      <div>For clinical use only</div>
    </div>
  
    <script>window.onload = () => window.print();</script>
  </body>
  </html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
}